import { useState, useRef, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import { Button } from '../Button';
import { Link } from 'react-router-dom';

type AccordionProps = {
  title: string | React.ReactNode;
  icon?: React.ReactNode;
  active?: boolean;
  children?: React.ReactNode;
  containerClassName?: string;
  triggerWrapperClassName?: string;
  titleClassName?: string;
  defaultIconClassName?: string;
  dragIconClassName?: string;
  bodyContainerClassName?: string;
  bodyClassName?: string;
  draggable?: boolean;
  onDeleteDisabled?: boolean;
  onClick?: () => void;
  linkForEdit?: string;
  onDelete?: () => void;
  onDragStart?: (event: React.DragEvent<HTMLElement>) => void;
  onDragOver?: (event: React.DragEvent<HTMLElement>) => void;
  onDragLeave?: (event: React.DragEvent<HTMLElement>) => void;
  onDrop?: (event: React.DragEvent<HTMLElement>) => void;
};

export const Accordion = ({
  title,
  icon,
  active = false,
  children,
  containerClassName,
  triggerWrapperClassName,
  titleClassName,
  defaultIconClassName,
  dragIconClassName,
  bodyContainerClassName,
  bodyClassName,
  onClick,
  linkForEdit,
  onDelete,
  onDeleteDisabled,
  draggable,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
}: AccordionProps) => {
  const [isOpen, setIsOpen] = useState(active);
  const contentRef = useRef<HTMLDivElement>(null);

  // Sync internal state with active prop
  useEffect(() => {
    setIsOpen(active);
  }, [active]);

  return (
    <div
      data-accordion="true"
      data-accordion-expand-all="false"
      className="flex items-stretch rounded-lg"
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <div
        className={twMerge(
          'accordion-item flex flex-1 [&:not(:last-child)]:border-b border-b-gray-200',
          containerClassName
        )}
        data-accordion-item="true"
      >
        <div
          className={twMerge(
            'accordion-toggle px-5 py-2.5 flex justify-between items-start w-full bg-blue-131025 rounded-md hover:cursor-pointer',
            draggable && 'hover:cursor-grab',
            triggerWrapperClassName
          )}
          onClick={() => {
            // If onClick is provided, let the parent control the state
            // Otherwise, manage internal state
            if (onClick) {
              onClick();
            } else {
              setIsOpen(!isOpen);
            }
          }}
        >
          <div className="w-full flex items-center gap-2">
            <div className="flex flex-1 items-center gap-2">
              {children && (
                <div className="flex items-center gap-1.5">
                  {icon || (
                    <div>
                      <i
                        className={twMerge(
                          'ki-outline ki-up-square text-white text-2xl',
                          isOpen ? 'rotate-180' : '',
                          defaultIconClassName
                        )}
                      />
                    </div>
                  )}
                  {draggable && (
                    <div className="inline-flex justify-center items-center">
                      <i
                        className={twMerge(
                          'ki-outline ki-arrow-two-diagonals text-[16px]',
                          dragIconClassName
                        )}
                      />
                    </div>
                  )}
                </div>
              )}
              <div
                className={twMerge(
                  'text-white text-sm font-extrabold',
                  titleClassName
                )}
              >
                {title}
              </div>
            </div>
            {linkForEdit && (
              <Link to={linkForEdit} className="mt-1.5">
                <i className="ki-outline ki-notepad-edit !text-black/80 font-medium !text-[18px]" />
              </Link>
            )}
            {onDelete && (
              <Button
                buttonClassName="bg-transparent h-auto p-0.5 btn-sm btn-icon-xl btn-clear btn-light p-0"
                leftIcon={
                  // <i className="ki-outline ki-cross-square !text-gray-800 hover:!text-red-FF2929" />
                  <i className="ki-outline ki-trash !text-black font-medium !text-[17.5px] hover:!text-red-FF0000" />
                }
                disabled={onDeleteDisabled}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
              />
            )}
          </div>
        </div>
        {children && (
          <div
            ref={contentRef}
            className={twMerge(
              'accordion-content max-h-max overflow-y-auto scrollbar scrollbar-thumb-gray-400 scrollbar-track-gray-200 transition-all duration-300 ease-in-out',
              !isOpen && 'hidden !max-h-0',
              bodyContainerClassName
            )}
          >
            <div className={twMerge('m-4', bodyClassName)}>{children}</div>
          </div>
        )}
      </div>
    </div>
  );
};
